import { SwiperItem } from '@app/components/swiper/type';
import { GALLERY_ORDER, NEW_TO_OLD_NAMING_MAP } from '../constants';

interface GalleryImage {
    src: string;
    alt?: string;
    height?: number;
    width?: number;
}

interface GalleryVideo {
    type: string;
    filename?: string;
    src?: string;
    videoSources: Array<{ url: string }>;
}

/**
 * Finds an item that matches the given pattern in its source/filename
 */
function findItemByPattern<T extends { src?: string; filename?: string }>(
    items: T[],
    pattern: string
): T | undefined {
    if (!items?.length || !pattern) return undefined;

    const normalizedPattern = pattern.toLowerCase();
    return items.find((item) => {
        const searchString = (item.filename || item.src || '').toLowerCase();
        return searchString.includes(normalizedPattern);
    });
}

/**
 * Creates a swiper item from an image
 */
function createImageItem(image: GalleryImage): SwiperItem {
    return {
        productImage: [
            {
                desktopImage: image.src,
                mobileImage: image.src,
                altText: image.alt || '',
                height: image.height || 0,
                width: image.width || 0,
                link: null,
            },
        ],
    };
}

/**
 * Creates a swiper item from a video
 */
function createVideoItem(video: GalleryVideo): SwiperItem | null {
    const videoSource = video.videoSources?.[0];
    if (!videoSource?.url) return null;

    return {
        poster: video.src || '',
        productVideo: [
            {
                desktopVideoUrl: videoSource.url,
                mobileVideoUrl: videoSource.url,
                controls: false,
                autoplay: true,
                muted: true,
                loop: true,
            },
        ],
    };
}

/**
 * Attempts to find a matching video for the given pattern
 */
function findMatchingVideo(
    videos: GalleryVideo[] | undefined,
    pattern: string
): GalleryVideo | undefined {
    if (!videos?.length) return undefined;

    // Try to find video with filename matching pattern
    const videoWithFilename = videos.find(
        (v) => v.type === 'VIDEO' && v.filename
    );

    if (videoWithFilename) {
        return findItemByPattern(
            videos.filter((v) => v.type === 'VIDEO' && v.filename),
            pattern
        );
    }

    // Fallback: find any video matching pattern by type
    return findItemByPattern(videos, pattern);
}

export function createGalleryItems(
    images: any[],
    videos: any[] | undefined
): SwiperItem[] {
    if (!images?.length) return [];

    return GALLERY_ORDER.reduce((acc: SwiperItem[], pattern) => {
        // Try to find matching image first (images get priority)
        const matchingImage =
            findItemByPattern(images, pattern) ||
            findItemByPattern(images, NEW_TO_OLD_NAMING_MAP[pattern]);

        if (matchingImage) {
            acc.push(createImageItem(matchingImage));
            return acc;
        }

        // If no image found, try to find matching video
        const matchingVideo = findMatchingVideo(videos, pattern);
        if (matchingVideo) {
            const videoItem = createVideoItem(matchingVideo);
            if (videoItem) {
                acc.push(videoItem);
            }
            return acc;
        }

        // Fallback: if pattern is "VIDEO" and no video with filename found, grab any available video
        if (pattern === 'VIDEO' && videos?.length) {
            const hasVideoWithFilename = videos.some(
                (v) => v.type === 'VIDEO' && v.filename
            );

            if (!hasVideoWithFilename) {
                const anyVideo = videos.find((v) => v.type === 'VIDEO');
                if (anyVideo) {
                    const videoItem = createVideoItem(anyVideo);
                    if (videoItem) {
                        acc.push(videoItem);
                    }
                }
            }
        }

        return acc;
    }, [] as SwiperItem[]);
}
